import React, { useState, useEffect, useRef } from 'react';
import leafIcon from '../assets/leaf-icon.png';
import './HeroNewsSlider.css';
import { API_BASE } from '../utils/api';

const HeroNewsCard = ({ item, idx, onInteract }) => (
  <a href={item.id ? `/news/${item.id}` : '/news'} className="hero-news-card" onClick={onInteract}>
    <div className="hero-news-card-acorn-left">
      <img src={leafIcon} alt="Meşe Palamudu" className="hero-news-card-acorn-icon" />
    </div>
    <div className="border-anim border-top"></div>
    <div className="border-anim border-right"></div>
    <div className="border-anim border-bottom"></div>
    <div className="border-anim border-left"></div>
    <div className="hero-news-card-content">
      <h3 className="hero-news-card-title">{item.title}</h3>
      <p className="hero-news-card-description">{item.description}</p>
      <span className="hero-news-card-link-icon" aria-hidden>›</span>
    </div>
  </a>
);

function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const HeroNewsSlider = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 640 && window.innerWidth <= 900);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [newsData, setNewsData] = useState([]);
  const autoSlideRef = useRef();
  const sliderRef = useRef();
  const touchStartX = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/news`)
      .then((r) => r.json())
      .then((data) => { if (data.success) setNewsData(data.items); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
      setIsTablet(window.innerWidth > 640 && window.innerWidth <= 900);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine group size
  let groupSize = 3; // mobilde 3 kart
  if (isTablet) groupSize = 4;
  if (!isMobile && !isTablet) groupSize = 6;

  // Grupları oluştur
  const groups = chunkArray(newsData, groupSize);
  const totalGroups = groups.length;

  // Auto-advance for all devices
  useEffect(() => {
    if (isPaused || totalGroups === 0) return;
    autoSlideRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalGroups);
    }, 4000);
    return () => clearInterval(autoSlideRef.current);
  }, [totalGroups, isPaused]);

  if (newsData.length === 0) return null;

  const handlePrev = () => {
    setIsPaused(true);
    setCurrentIndex((prev) => (prev === 0 ? totalGroups - 1 : prev - 1));
  };

  const handleNext = () => {
    setIsPaused(true);
    setCurrentIndex((prev) => (prev + 1) % totalGroups);
  };

  // Touch swipe for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (deltaX > 40) {
      // Swipe right (önceki gruba)
      setCurrentIndex((prev) => prev === 0 ? totalGroups - 1 : prev - 1);
    } else if (deltaX < -40) {
      // Swipe left (sonraki gruba)
      setCurrentIndex((prev) => (prev + 1) % totalGroups);
    }
    touchStartX.current = null;
  };

  // Mobil görünüm
  if (isMobile) {
    return (
      <div className="hero-news-slider-wrapper" style={{minHeight: '460px', overflow: 'visible', position: 'relative'}}>
        <div
          className="hero-news-mobile-slider"
          ref={sliderRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: `${totalGroups * 100}%`,
            transform: `translateX(-${currentIndex * (100 / totalGroups)}%)`,
            transition: 'transform 0.5s cubic-bezier(0.4,0,0.2,1)',
          }}
          onTouchStart={(e) => { setIsPaused(true); handleTouchStart(e); }}
          onTouchEnd={handleTouchEnd}
        >
          {groups.map((group, groupIdx) => (
            <div
              key={groupIdx}
              style={{
                width: `${100 / totalGroups}%`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
              }}
            >
              {group.map((item, idx) => (
                <HeroNewsCard item={item} idx={groupIdx * groupSize + idx} key={groupIdx * groupSize + idx} onInteract={() => setIsPaused(true)} />
              ))}
            </div>
          ))}
        </div>
        <button className="hero-news-arrow hero-news-arrow-left" onClick={handlePrev} aria-label="Önceki">‹</button>
        <button className="hero-news-arrow hero-news-arrow-right" onClick={handleNext} aria-label="Sonraki">›</button>
      </div>
    );
  }

  // Tablet & Web: auto-advance grid
  return (
    <div className="hero-news-slider-wrapper" style={{overflow: 'hidden', position: 'relative'}}>
      <div
        className="hero-news-grid"
        ref={sliderRef}
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: `${totalGroups * 100}%`,
          transform: `translateX(-${currentIndex * (100 / totalGroups)}%)`,
          transition: 'transform 0.5s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {groups.map((group, groupIdx) => (
          <div
            key={groupIdx}
            style={{
              width: `${100 / totalGroups}%`,
              display: 'grid',
              gridTemplateColumns: isTablet ? '1fr 1fr' : '1fr 1fr 1fr',
              gridTemplateRows: '1fr 1fr',
              justifyItems: 'center',
              alignItems: 'center',
              gap: '1.5rem',
            }}
          >
            {group.map((item, idx) => (
              <HeroNewsCard item={item} idx={groupIdx * groupSize + idx} key={groupIdx * groupSize + idx} onInteract={() => setIsPaused(true)} />
            ))}
          </div>
        ))}
      </div>
      <button className="hero-news-arrow hero-news-arrow-left" onClick={handlePrev} aria-label="Önceki">‹</button>
      <button className="hero-news-arrow hero-news-arrow-right" onClick={handleNext} aria-label="Sonraki">›</button>
    </div>
  );
};

export default HeroNewsSlider; 